import React, { useEffect, useState } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  User,
  Phone,
  Mail,
  MapPin,
  Search,
  Filter,
  Download,
  Save,
  X,
  Users,
  UserPlus,
  UserCheck,
  Share2
} from 'lucide-react';
import { clientsAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  contact: '',
  phone: '',
  address: '',
  referred_by: '',
};

const referredByOptions = [
  { value: 'google', label: 'Google' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'voisin', label: 'Voisin' },
  { value: 'passager', label: 'Passager' },
  { value: 'ancien client', label: 'Ancien client' },
  { value: 'reco ancien client', label: 'Reco ancien client' },
  { value: 'reco medecin', label: 'Reco medecin' },
  { value: 'ami abdou', label: 'Ami abdou' },
  { value: 'chat gpt', label: 'Chat GPT' },
  { value: 'bni', label: 'BNI' },
  { value: 'convention', label: 'Convention' },
  { value: 'autre', label: 'Autre' },
];

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch clients
  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await clientsAPI.getClients();
      setClients(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des clients');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle select change (for react-select components)
  const handleSelectChange = (selectedOption, name) => {
    setForm((f) => ({ ...f, [name]: selectedOption ? selectedOption.value : '' }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await clientsAPI.updateClient(editingId, form);
        toast.success('Client modifié');
      } else {
        await clientsAPI.createClient(form);
        toast.success('Client ajouté');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchClients();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (client) => {
    const referredByValue = client.referred_by ? String(client.referred_by).trim() : '';
    setForm({
      name: client.name || '',
      contact: client.contact || '',
      phone: client.phone || '',
      address: client.address || '',
      referred_by: referredByValue,
    });
    setEditingId(client.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await clientsAPI.deleteClient(id);
      toast.success('Client supprimé');
      fetchClients();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  const filteredClients = clients.filter(client =>
    String(client.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(client.contact || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(client.phone || '').includes(searchTerm) ||
    String(client.referred_by || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tableColumns = [
    {
      key: 'name',
      label: 'Client',
      render: (value, client) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
            <User className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{client.name}</div>
            {client.contact && (
              <div className="text-sm text-neutral-500">Contact: {client.contact}</div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (value) => (
        <div className="flex items-center">
          <Phone className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'referred_by',
      label: 'De la part de',
      render: (value) => (
        <div className="flex items-center">
          <Share2 className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'address',
      label: 'Adresse',
      render: (value) => (
        <div className="flex items-center">
          <MapPin className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, client) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(client)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(client.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Clients</h1>
          <p className="text-neutral-600">Gérez votre base de données clients</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des clients..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter un client
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Clients</p>
              <p className="text-2xl font-bold text-neutral-900">{clients.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Téléphone</p>
              <p className="text-2xl font-bold text-success-600">
                {clients.filter(c => c.phone).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Phone className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Adresse</p>
              <p className="text-2xl font-bold text-warning-600">
                {clients.filter(c => c.address).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Nouveaux</p>
              <p className="text-2xl font-bold text-info-600">
                {clients.filter(c => {
                  const createdDate = new Date(c.created_at || Date.now());
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return createdDate > weekAgo;
                }).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Avec Référence</p>
              <p className="text-2xl font-bold text-purple-600">
                {clients.filter(c => c.referred_by).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Share2 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Clients Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des clients</h3>
            <p className="text-sm text-neutral-600">{filteredClients.length} clients trouvés</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredClients}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier le client' : 'Ajouter un client'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom du client"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Ex: Jean Dupont"
              icon={<User className="w-5 h-5" />}
              required
            />

            <ModernInput
              label="Contact"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              placeholder="Ex: Marie Dupont"
              icon={<UserCheck className="w-5 h-5" />}
            />

            <ModernInput
              label="Téléphone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Ex: +33 1 23 45 67 89"
              icon={<Phone className="w-5 h-5" />}
            />

            <ModernSelect
              label="De la part de"
              name="referred_by"
              options={referredByOptions}
              value={form.referred_by || null}
              onChange={(selectedOption) => handleSelectChange(selectedOption, 'referred_by')}
              placeholder="Sélectionner une option"
              isClearable
            />

            <ModernInput
              label="Adresse"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="Ex: 123 Rue de la Paix, Paris"
              icon={<MapPin className="w-5 h-5" />}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Clients; 